import {AfterViewInit, Component, ElementRef, Inject} from '@angular/core';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors} from '@angular/forms';
import {AudioErrorsComponent} from './audio-errors.component';
import {DlCheckboxComponent} from '../dl-checkbox/dl-checkbox.component';
import {AudioRow} from './encoding-ladder.model';

export interface AudioRenditionDialogData {
  headline: string;
  actionName: string;
  /**
   * The row's own control, edited in place: the rules across rows read its siblings, which a
   * detached copy could not see. The caller restores or removes it on cancel.
   */
  row: FormGroup;
  /** Whether the current audio mode writes this column; the others are shown disabled, as in the table. */
  applies: (column: keyof AudioRow) => boolean;
  /** The codecs this row may pick, given its channel count. */
  codecsFor: (row: AbstractControl) => string[];
  codecLabel: (codec: string) => string;
  /** The rules across all rows, read live. */
  setErrors: () => ValidationErrors | null;
  /** Show errors on opening; a row just added waits until the operator has touched a field. */
  revealErrors: boolean;
}

/** Closes with `true` when the operator confirms, `false` (or nothing) when they cancel. */
@Component({
  selector: 'app-audio-rendition-dialog',
  templateUrl: './audio-rendition-dialog.component.html',
  styleUrl: './scrolling-dialog.scss',
  imports: [ReactiveFormsModule, AudioErrorsComponent, DlCheckboxComponent],
})
export class AudioRenditionDialogComponent implements AfterViewInit {
  constructor(public dialogRef: DialogRef<boolean>,
              @Inject(DIALOG_DATA) public data: AudioRenditionDialogData,
              private host: ElementRef<HTMLElement>) {
  }

  /**
   * Disables the columns the audio mode does not write. Done on the elements, not with an
   * `[attr.disabled]` binding: the reactive-forms directive writes the enabled state of its own
   * input after that binding has run, and would quietly undo it. The controls stay enabled, so the
   * row's value and validity are what the table shows.
   */
  ngAfterViewInit(): void {
    const columns: (keyof AudioRow)[] = ['channels', 'codec', 'bitrate', 'samplerate', 'display', 'required', 'allow_downmix'];
    for (const column of columns.filter(c => !this.data.applies(c))) {
      (this.host.nativeElement.querySelector(`#audio_${column}`) as HTMLInputElement).disabled = true;
    }
  }

  readonly bitrateHint = 'Use a whole number with an optional k or M suffix, for example 128k.';

  get row(): FormGroup { return this.data.row; }

  get showErrors(): boolean { return this.data.revealErrors || this.row.dirty || this.row.touched; }
}
